import React, { useEffect, useMemo, useState } from "react";
import api, { downloadFile } from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { ServiceWatch, RemediationEvent, Device } from "../types";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import ActionButton from "../components/ActionButton";
import { formatDateTime } from "../utils/datetime";

const RESULT_COLORS: Record<string, string> = {
  success: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  rate_limited: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  skipped: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

export default function Remediation() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "technician";
  const isAdmin = user?.role === "admin";

  const { currentCustomerId } = useCustomer();
  // The global customer switcher is the single source of truth — no local picker.
  const customerId = currentCustomerId;
  const [watches, setWatches] = useState<ServiceWatch[]>([]);
  const [events, setEvents] = useState<RemediationEvent[]>([]);
  const [devices, setDevices] = useState<Record<number, Device>>({});
  const [days, setDays] = useState(7);
  const [resultFilter, setResultFilter] = useState<string>("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const watchSort = useSort("service_name");
  const eventSort = useSort("created_at");

  const [newService, setNewService] = useState({
    service_name: "",
    display_name: "",
    max_restarts_per_hour: 3,
  });
  // Populate the service-name field from a device's installed services so the
  // user picks from a dropdown instead of guessing the exact service key.
  const [svcDeviceId, setSvcDeviceId] = useState<number | "">("");
  const [deviceServices, setDeviceServices] = useState<{ name: string; display_name: string | null }[]>([]);
  const [svcScanning, setSvcScanning] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    loadWatches();
    loadEvents();
    api.get("/api/v1/devices", { params: { customer_id: customerId } }).then((r) => {
      const map: Record<number, Device> = {};
      r.data.forEach((d: Device) => {
        map[d.id] = d;
      });
      setDevices(map);
    });
    // eslint-disable-next-line
  }, [customerId, days, resultFilter]);

  const loadWatches = async () => {
    if (!customerId) return;
    const r = await api.get("/api/v1/remediation/watches", { params: { customer_id: customerId } });
    setWatches(r.data);
  };

  const loadEvents = async () => {
    if (!customerId) return;
    setError("");
    try {
      const params: any = { customer_id: customerId, days };
      if (resultFilter) params.result = resultFilter;
      const r = await api.get("/api/v1/remediation/events", { params });
      setEvents(r.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load events");
    }
  };

  const addWatch = async () => {
    if (!customerId || !newService.service_name.trim()) return;
    setError("");
    try {
      await api.post("/api/v1/remediation/watches", {
        customer_id: customerId,
        service_name: newService.service_name.trim(),
        display_name: newService.display_name.trim() || undefined,
        max_restarts_per_hour: newService.max_restarts_per_hour,
      });
      setNewService({ service_name: "", display_name: "", max_restarts_per_hour: 3 });
      loadWatches();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to add watch");
    }
  };

  const loadDeviceServices = async (deviceId: number) => {
    try {
      const r = await api.get(`/api/v1/devices/${deviceId}/services/cached`);
      setDeviceServices(r.data?.services || []);
    } catch {
      setDeviceServices([]);
    }
  };

  const scanDeviceServices = async () => {
    if (!svcDeviceId) return;
    setSvcScanning(true);
    setError("");
    try {
      await api.post(`/api/v1/devices/${svcDeviceId}/services/list`);
      // Poll the cached list until the live scan lands (device must be online).
      for (let i = 0; i < 15; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const r = await api.get(`/api/v1/devices/${svcDeviceId}/services/cached`);
        if ((r.data?.services || []).length) {
          setDeviceServices(r.data.services);
          break;
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "Couldn't scan services (device may be offline)");
    } finally {
      setSvcScanning(false);
    }
  };

  const toggleWatch = async (w: ServiceWatch) => {
    await api.put(`/api/v1/remediation/watches/${w.id}`, { enabled: !w.enabled });
    loadWatches();
  };

  const deleteWatch = async (w: ServiceWatch) => {
    if (!window.confirm(`Stop watching service "${w.service_name}"?`)) return;
    await api.delete(`/api/v1/remediation/watches/${w.id}`);
    loadWatches();
  };

  const seedDefaults = async () => {
    if (!customerId) return;
    await api.post(`/api/v1/remediation/watches/${customerId}/seed-defaults`);
    loadWatches();
  };

  const sendReportNow = async () => {
    if (!window.confirm("Send the weekly self-healing report to the configured alert address now?"))
      return;
    setInfo("");
    try {
      await api.post("/api/v1/remediation/report/send-now");
      setInfo("Report queued. It will be sent in the next few seconds.");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to queue report");
    }
  };

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly - see the note in
  // utils/aiScreenContext: machine output and credential fields must
  // not reach a prompt.
  usePublishScreenData(
    "Self-healing events",
    useMemo(
      () =>
        events.map((e) => ({
          id: e.id,
          action: e.action,
          target: e.target ?? null,
          result: e.result,
          when: e.created_at,
          duration_ms: e.duration_ms ?? null,
          error: e.error_message ?? null,
          // NOT `details` - a free-form dict from whatever produced the event.
        })),
      [events],
    ),
    `${watches.length} service watch(es) configured`,
  );

  const summary = useMemo(() => {
    return {
      total: events.length,
      success: events.filter((e) => e.result === "success").length,
      failed: events.filter((e) => e.result === "failed").length,
      rate_limited: events.filter((e) => e.result === "rate_limited").length,
    };
  }, [events]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Self-Healing</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const params: any = { days };
              if (customerId) params.customer_id = customerId;
              downloadFile("/api/v1/exports/remediation-events.pdf", "self-healing.pdf", params);
            }}
            className="border dark:border-gray-600 text-sm px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            PDF
          </button>
          <button
            onClick={() => {
              const params: any = { days };
              if (customerId) params.customer_id = customerId;
              downloadFile(
                "/api/v1/exports/remediation-events.csv",
                "remediation-events.csv",
                params,
              );
            }}
            className="border dark:border-gray-600 text-sm px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            CSV
          </button>
          {isAdmin && (
            <ActionButton onAction={() => sendReportNow()} variant="raw" className="border dark:border-gray-600 text-sm px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40" pendingLabel="Sending…">
              Send weekly report now
            </ActionButton>
          )}
        </div>
      </div>

      {info && <div className="mb-3 text-xs text-blue-600 dark:text-blue-400">{info}</div>}
      {error && <div className="mb-3 text-sm text-red-500 dark:text-red-400">{error}</div>}

      {!customerId && (
        <div className="mb-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-200">
          Select a customer in the header switcher to view and manage its self-healing service
          watches and events.
        </div>
      )}

      <div className="grid grid-cols-4 gap-3 mb-6">
        <SummaryCard
          label="Events"
          value={summary.total}
          color="text-gray-900 dark:text-gray-100"
          bg="bg-gray-50 dark:bg-gray-900/50"
        />
        <SummaryCard
          label="Successful fixes"
          value={summary.success}
          color="text-green-600 dark:text-green-400"
          bg="bg-green-50 dark:bg-green-900/30"
        />
        <SummaryCard
          label="Failed"
          value={summary.failed}
          color="text-red-600 dark:text-red-400"
          bg="bg-red-50 dark:bg-red-900/30"
        />
        <SummaryCard
          label="Rate-limited"
          value={summary.rate_limited}
          color="text-yellow-600 dark:text-yellow-400"
          bg="bg-yellow-50 dark:bg-yellow-900/30"
        />
      </div>

      {/* Watched services */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto mb-8">
        <div className="px-4 py-3 border-b dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Watched Services</h2>
          {canEdit && watches.length === 0 && (
            <ActionButton onAction={() => seedDefaults()} variant="raw" className="text-xs text-blue-600 dark:text-blue-400 hover:underline" pendingLabel="Working…">
              Seed standard defaults
            </ActionButton>
          )}
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Service" sortKey="service_name" sort={watchSort.sort} onSort={watchSort.toggle} />
              <SortableTh label="Display Name" sortKey="display_name" sort={watchSort.sort} onSort={watchSort.toggle} />
              <SortableTh label="Max restarts/hr" sortKey="max_restarts_per_hour" sort={watchSort.sort} onSort={watchSort.toggle} />
              <SortableTh label="Status" sortKey="enabled" sort={watchSort.sort} onSort={watchSort.toggle} />
              <th className="px-4 py-2 text-right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {watches.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                  No services watched yet for this customer. Click "Seed standard defaults" or add
                  manually below.
                </td>
              </tr>
            )}
            {sortRows(watches, watchSort.sort, (w, key) => {
              switch (key) {
                case "display_name":
                  return (w.display_name || "").toLowerCase();
                case "max_restarts_per_hour":
                  return w.max_restarts_per_hour ?? 0;
                case "enabled":
                  return w.enabled ? 1 : 0;
                default:
                  return (w.service_name || "").toLowerCase();
              }
            }).map((w) => (
              <tr key={w.id}>
                <td className="px-4 py-2 font-mono text-xs">{w.service_name}</td>
                <td className="px-4 py-2">{w.display_name || "—"}</td>
                <td className="px-4 py-2">{w.max_restarts_per_hour}</td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${w.enabled ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}
                  >
                    {w.enabled ? "Active" : "Paused"}
                  </span>
                </td>
                <td className="px-4 py-2 text-right space-x-3">
                  {canEdit && (
                    <button
                      onClick={() => toggleWatch(w)}
                      className="text-xs text-blue-500 hover:underline"
                    >
                      {w.enabled ? "Pause" : "Resume"}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => deleteWatch(w)}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEdit && (
          <div className="px-4 py-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center gap-2 flex-wrap">
            <select
              value={svcDeviceId}
              onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : "";
                setSvcDeviceId(id);
                setDeviceServices([]);
                if (id) loadDeviceServices(id as number);
              }}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              title="Pick a device to suggest its installed services"
            >
              <option value="">Suggest from device…</option>
              {Object.values(devices).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.hostname}
                </option>
              ))}
            </select>
            {svcDeviceId !== "" && deviceServices.length === 0 && (
              <button
                onClick={scanDeviceServices}
                disabled={svcScanning}
                className="text-xs border dark:border-gray-600 rounded-lg px-3 py-2 text-gray-600 dark:text-gray-300 disabled:opacity-50"
                title="Query the device now for its current services"
              >
                {svcScanning ? "Scanning…" : "Scan services"}
              </button>
            )}
            <input
              placeholder="Service name (e.g. Spooler)"
              list="svc-suggestions"
              value={newService.service_name}
              onChange={(e) => {
                const v = e.target.value;
                const m = deviceServices.find((s) => s.name.toLowerCase() === v.toLowerCase());
                setNewService((n) => ({
                  ...n,
                  service_name: v,
                  display_name: m?.display_name || n.display_name,
                }));
              }}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm w-48"
            />
            <datalist id="svc-suggestions">
              {deviceServices.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.display_name || s.name}
                </option>
              ))}
            </datalist>
            <input
              placeholder="Display name (optional)"
              value={newService.display_name}
              onChange={(e) => setNewService({ ...newService, display_name: e.target.value })}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm flex-1"
            />
            <input
              type="number"
              min={1}
              max={20}
              value={newService.max_restarts_per_hour}
              onChange={(e) =>
                setNewService({ ...newService, max_restarts_per_hour: Number(e.target.value) })
              }
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm w-20"
              title="Max restarts per hour"
            />
            <ActionButton onAction={() => addWatch()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700" pendingLabel="Adding…">
              Add
            </ActionButton>
          </div>
        )}
      </div>

      {/* Events */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <div className="px-4 py-3 border-b dark:border-gray-700 flex items-center gap-2 justify-between">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Recent Activity</h2>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
            >
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value)}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
            >
              <option value="">All results</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="rate_limited">Rate-limited</option>
            </select>
          </div>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="When" sortKey="created_at" sort={eventSort.sort} onSort={eventSort.toggle} />
              <SortableTh label="Device" sortKey="device" sort={eventSort.sort} onSort={eventSort.toggle} />
              <SortableTh label="Action" sortKey="action" sort={eventSort.sort} onSort={eventSort.toggle} />
              <SortableTh label="Target" sortKey="target" sort={eventSort.sort} onSort={eventSort.toggle} />
              <SortableTh label="Duration" sortKey="duration_ms" sort={eventSort.sort} onSort={eventSort.toggle} />
              <SortableTh label="Result" sortKey="result" sort={eventSort.sort} onSort={eventSort.toggle} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {events.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                  No self-healing events recorded.
                </td>
              </tr>
            )}
            {sortRows(events, eventSort.sort, (e, key) => {
              switch (key) {
                case "device":
                  return (
                    (e.device_id ? devices[e.device_id]?.hostname : "") || ""
                  ).toLowerCase();
                case "action":
                  return (e.action || "").toLowerCase();
                case "target":
                  return (e.target || "").toLowerCase();
                case "duration_ms":
                  return e.duration_ms ?? 0;
                case "result":
                  return (e.result || "").toLowerCase();
                default:
                  return e.created_at ? Date.parse(e.created_at) : 0;
              }
            }).map((e) => {
              const dev = e.device_id ? devices[e.device_id] : null;
              return (
                <tr key={e.id}>
                  <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(e.created_at)}
                  </td>
                  <td className="px-4 py-2">{dev?.hostname || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
                  <td className="px-4 py-2">{e.target || "—"}</td>
                  <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {e.duration_ms == null
                      ? "—"
                      : e.duration_ms < 1000
                        ? `${e.duration_ms} ms`
                        : `${(e.duration_ms / 1000).toFixed(1)} s`}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${RESULT_COLORS[e.result] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}
                    >
                      {e.result}
                    </span>
                    {e.error_message && (
                      <div className="text-xs text-red-500 dark:text-red-400 mt-0.5">{e.error_message}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  color,
  bg,
}: {
  label: string;
  value: number;
  color: string;
  bg: string;
}) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-gray-100 dark:border-gray-700`}>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mt-1">{label}</div>
    </div>
  );
}
